import { Fragment, useState, useMemo } from "react";
import {
  Grid,
  Tabs,
  Tab as MuiTab,
  List,
  ListItem as MuiListItem,
  CircularProgress,
  Box,
  useTheme,
  styled,
} from "@mui/material";
import { yellow, orange } from "@mui/material/colors";
import { useApi } from "@platform/api";
import { paletteVar, Typography } from "@platform/design-system";

import { TabPanel } from "../../../../components";
import RequiredTypography from "../../../../components/RequiredTypography";
import { getPercentageUploadedDocs } from "../../utils";
import { FileDownloadTypography } from "../../components/styledComponents";
import { downloadBinaryFile } from "../../../../utils";
import { useToastNotificationService } from "../../../../hooks";
import { sortProductsByCoverage } from "../../../../pages/RFP/utils";

export const CustomScrollbarBox = styled(Box)(({ theme }) => ({
  overflowY: "scroll",
  maxHeight: "300px",
  boxShadow: "none",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.brandSupporting.main,
  },
  "&::-webkit-scrollbar-track": {
    background: paletteVar("surfaces-neutral", "#f2f2f2"),
  },
}));

const SectionChecklist = ({ documentTypes, progressValue, uploadedFiles }) => {
  const theme = useTheme();
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();

  const [tabIndex, setTabIndex] = useState(0);
  const percentageOptionalUploadedDocs = useMemo(
    () => getPercentageUploadedDocs(documentTypes, "optional"),
    [documentTypes],
  );

  const handleDownload = async (file) => {
    try {
      if (file instanceof File) {
        downloadBinaryFile(file, file.name);
      } else {
        const { data } = await axiosInstance.post(
          `brokerportal/v1/getRfpFile`,
          {
            downloadLink: file?.file?.url,
          },
          {
            responseType: "blob",
          },
        );

        downloadBinaryFile(data, file.name);
      }
    } catch {
      createToastNotification({
        severity: "error",
        message: `Unable to download file ${file.name}. Please try again.`,
        title: "Couldn't download file",
      });
    }
  };

  return (
    <Grid
      size={4}
      px={2}
      borderLeft={`1px solid ${theme.palette.neutrals.neutral_300}`}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        sx={{ marginBottom: "20px" }}
      >
        <CircularProgressWithLabel
          value={progressValue}
          sx={{
            color:
              progressValue < 35
                ? orange[500]
                : progressValue < 70
                  ? yellow[500]
                  : theme.palette.secondary.apple_green,
          }}
        />
        <Typography variant="body-medium">
          <strong>Document Upload Progress</strong>
        </Typography>
      </Box>

      <Tabs
        className="detail-page-tabs"
        value={tabIndex}
        onChange={(_, newValue) => setTabIndex(newValue)}
        aria-label="document checklist tabs"
      >
        <Tab label="Pending" {...a11yProps(0)} />
        <Tab label="Assigned" {...a11yProps(1)} />
        <Tab label="Files" {...a11yProps(2)} />
      </Tabs>
      <TabPanel index={0} value={tabIndex} sx={{ mt: 3, px: 2 }}>
        {progressValue === 100 && percentageOptionalUploadedDocs === 100 ? (
          <EmptyTypography>No pending documents</EmptyTypography>
        ) : (
          <DocumentTypesList
            documentTypes={documentTypes}
            showRequiredIndicator={true}
          />
        )}
      </TabPanel>
      <TabPanel index={1} value={tabIndex} sx={{ mt: 3, px: 2 }}>
        <DocumentTypesList
          documentTypes={documentTypes}
          showRequiredIndicator={false}
          showUploadedOnly={true}
        />
        <NoCoverageAssignmentMessage documentTypes={documentTypes} />
      </TabPanel>
      <TabPanel index={2} value={tabIndex} sx={{ mt: 3, px: 2 }}>
        <CustomScrollbarBox>
          {uploadedFiles.length > 0 ? (
            <List>
              {uploadedFiles.map((file) => (
                <MuiListItem key={file.name}>
                  <FileDownloadTypography onClick={() => handleDownload(file)}>
                    {file.name}
                  </FileDownloadTypography>
                </MuiListItem>
              ))}
            </List>
          ) : (
            <EmptyTypography>No documents added</EmptyTypography>
          )}
        </CustomScrollbarBox>
      </TabPanel>
    </Grid>
  );
};

function NoCoverageAssignmentMessage({ documentTypes }) {
  for (const documentType of documentTypes) {
    const hasRequired = documentType.coverages.required.some(
      (c) => c.files.length > 0,
    );
    const hasOptional = documentType.coverages.optional.some(
      (c) => c.files.length > 0,
    );
    if (hasRequired || hasOptional) {
      return null;
    }
  }

  return (
    <EmptyTypography>No documents have coverage assignments</EmptyTypography>
  );
}

function EmptyTypography({ children }) {
  return (
    <Typography variant="body-medium" color="textSecondary">
      <em>{children}</em>
    </Typography>
  );
}

function CircularProgressWithLabel(props) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress variant="determinate" size="3rem" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
        >{`${props.value}%`}</Typography>
      </Box>
    </Box>
  );
}

function DocumentTypesList({
  documentTypes,
  showRequiredIndicator,
  showUploadedOnly,
}) {
  return documentTypes.reduce((acc, documentType, i) => {
    let filteredRequired = documentType.coverages.required.filter((c) =>
      showUploadedOnly ? c.files.length > 0 : c.files.length === 0,
    );
    let filteredOptional = documentType.coverages.optional.filter((c) =>
      showUploadedOnly ? c.files.length > 0 : c.files.length === 0,
    );

    filteredOptional = sortProductsByCoverage(filteredOptional, "coverageName");
    filteredRequired = sortProductsByCoverage(filteredRequired, "coverageName");

    if (filteredRequired.length > 0 || filteredOptional.length > 0) {
      acc.push(
        <Fragment key={documentType.documentTypeName}>
          <Typography
            variant="body-medium"
            color="brandPrimary"
            sx={{ textTransform: "uppercase" }}
            mt={i === 0 ? 0 : 2.5}
          >
            <strong>{documentType.documentTypeName}</strong>
          </Typography>
          <List>
            {filteredRequired.map((c) => (
              <ListItem
                key={`required-${c.coverageName}`}
                coverageName={c.coverageName}
                isRequired={showRequiredIndicator}
              />
            ))}
            {filteredOptional.map((c) => (
              <ListItem
                key={`optional-${c.coverageName}`}
                coverageName={c.coverageName}
              />
            ))}
          </List>
        </Fragment>,
      );
    }
    return acc;
  }, []);
}

function ListItem({ coverageName, isRequired }) {
  return (
    <MuiListItem sx={{ py: 0.5 }}>
      <RequiredTypography
        fontSize="0.875rem"
        component="span"
        isRequired={isRequired}
      >
        {coverageName}
      </RequiredTypography>
    </MuiListItem>
  );
}

const Tab = styled(MuiTab)({
  opacity: 0.5,
  "&.Mui-selected": {
    opacity: 1,
  },
});

function a11yProps(index) {
  return {
    id: `document-checklist-tab-${index}`,
    "aria-controls": `document-checklist-tabpanel-${index}`,
  };
}

export default SectionChecklist;
